// One trip's Costs payload (GET /api/trips/:id/expenses), shared by the surfaces
// outside the Costs tab that need to know what's been logged: the itinerary's
// "logged" markers and the expense sheet the itinerary opens. Module-level so
// they share one fetch; bustable, not HTTP-cached (CLAUDE.md, JSON caching).
//
// The Costs tab hands over each payload it loads (primeTripCosts) — so its
// own writes, which reload, need nothing more. A write from anywhere else
// calls invalidateTripCosts(tripId). Cleared at an auth boundary
// (clearTripCostsCache, utils/cacheInvalidation).

import { useEffect, useState } from 'react';
import tripExpensesApi from '../services/tripExpensesApi';

const entries = new Map(); // tripId -> { data, fetchedAt, inflight, gen, listeners: Set }

// Someone else on the trip can log an expense: a planner opened again after
// this long asks again rather than show what it read before.
const STALE_MS = 60_000;

const entryFor = (tripId) => {
  if (!entries.has(tripId)) entries.set(tripId, { data: undefined, fetchedAt: 0, inflight: null, gen: 0, listeners: new Set() });
  return entries.get(tripId);
};

const notify = (entry) => entry.listeners.forEach((fn) => fn(entry.data));

function load(tripId) {
  const entry = entryFor(tripId);
  if (entry.inflight) return entry.inflight;
  const gen = entry.gen;
  entry.inflight = tripExpensesApi.getTripCosts(tripId).then((data) => {
    // An invalidation while this was on its way: what it fetched is older
    // than the write, so it is dropped and the newer load stands.
    if (entry.gen !== gen) return entry.data;
    entry.inflight = null;
    if (data) {
      entry.data = data;
      entry.fetchedAt = Date.now();
    } else if (entry.data === undefined) {
      // A refusal or a failed request (the API answers null to both). Not
      // stamped fresh: the next listener to mount asks again. A payload
      // already held is kept — one dropped request doesn't wipe the markers.
      entry.data = null;
    }
    notify(entry);
    return entry.data;
  });
  return entry.inflight;
}

/** The Costs tab's freshly loaded payload, for everyone else. A failed load hands over nothing. */
export function primeTripCosts(tripId, data) {
  if (!tripId || !data) return;
  const entry = entryFor(tripId);
  entry.gen += 1;
  entry.inflight = null;
  entry.data = data;
  entry.fetchedAt = Date.now();
  notify(entry);
}

/** After a ledger write outside the Costs tab: drop what's cached and refetch for whoever's listening. */
export function invalidateTripCosts(tripId) {
  if (!tripId || !entries.has(tripId)) return;
  const entry = entries.get(tripId);
  entry.gen += 1;
  entry.inflight = null;
  entry.data = undefined;
  if (entry.listeners.size > 0) load(tripId);
}

/** Auth boundary: a ledger read for one account is not the next one's. */
export function clearTripCostsCache() {
  for (const entry of entries.values()) {
    entry.gen += 1;
    entry.inflight = null;
    entry.data = undefined;
    entry.fetchedAt = 0;
    notify(entry);
  }
  // Entries with listeners stay (their hooks hold them); the rest go.
  for (const [tripId, entry] of entries) {
    if (entry.listeners.size === 0) entries.delete(tripId);
  }
}

/**
 * The trip's Costs payload: undefined while loading, null when the viewer
 * may not read it (or it failed), else the payload. `enabled` false (no
 * trip yet, not a member, a child) asks nothing and answers null.
 */
export function useTripCosts(tripId, enabled = true) {
  const active = !!tripId && enabled;
  const [data, setData] = useState(() => {
    if (!active) return null;
    const held = entries.get(tripId)?.data;
    return held === null ? undefined : held; // a failed read is asked again (below)
  });

  useEffect(() => {
    if (!active) { setData(null); return undefined; }
    const entry = entryFor(tripId);
    const listener = (next) => setData(next);
    entry.listeners.add(listener);
    // A failed read before this one asks again, and reads as loading till it
    // lands: the itinerary's sheet waits for it rather than opening with no
    // trip currency.
    setData(entry.data === null ? undefined : entry.data);
    if (entry.data == null || Date.now() - entry.fetchedAt > STALE_MS) load(tripId);
    return () => { entry.listeners.delete(listener); };
  }, [tripId, active]);

  return data;
}
